import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { PrestadorRecursosHumanosService } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-humanos.service';
import { PrestadorRecursoHumano, PrestadorRecursoHumanoRequest } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-humanos.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-recursos-humanos-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, BotaoAcoes, Button, CardContent, ConfirmDialog, CustomTableComponent, Dialog, InputNumber, Select],
    providers: [ConfirmationService],
    templateUrl: './recursos-humanos-prestador.html',
    styleUrls: ['./recursos-humanos-prestador.scss']
})
export class RecursosHumanosPrestador {
    pessoa = input<Pessoa>();

    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorRecursosHumanosService);
    private readonly confirmation = inject(ConfirmationService);
    private readonly notificacoes = inject(NotificacoesService);
    readonly linhas = signal<PrestadorRecursoHumano[]>([]);
    readonly linhasTabela = computed(() => this.linhas().map((item) => ({ ...item, dtInclusao: DateUtils.formatDate(item.dtInclusao) })));
    readonly tipos = signal<PrestadorRecursoHumano[]>([]);
    readonly carregando = signal(false);
    readonly salvando = signal(false);
    editando = false;
    itemEditado?: PrestadorRecursoHumano;
    readonly colunaOrdem = ['recursoHumano', 'quantidade', 'dtInclusao'];
    protected readonly roleEditar = PERMISSOES.prestadores.recursosHumanos.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({ id: 'novo', label: 'Novo', icon: 'pi pi-plus', cor: 'novo', permission: this.roleEditar, command: () => this.novo() }),
            acaoMenuItem({ id: 'atualizar', label: 'Atualizar', icon: 'pi pi-refresh', cor: 'info', disabled: this.carregando(), command: () => this.carregar() })
        ];
    }

    readonly metadata: TableMetadata = {
        headers: {
            recursoHumano: { type: 'text', value: 'Tipo de Recurso Humano', default: '-', hidden: false },
            quantidade: { type: 'text', value: 'Quantidade', default: '-', hidden: false },
            dtInclusao: { type: 'text', value: 'Inclusão', default: '-', hidden: true }
        },
        title: 'recursoHumano',
        actions: {
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'info', command: (item: PrestadorRecursoHumano) => this.editar(item), permission: this.roleEditar },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: PrestadorRecursoHumano) => this.confirmarRemocao(item), permission: this.roleEditar }
        },
        defaultSortField: 'recursoHumano',
        defaultSortOrder: 1
    };
    readonly form = this.fb.group({ idRecursoHumano: this.fb.control<number | null>(null, Validators.required), quantidade: this.fb.control<number | null>(1, [Validators.required, Validators.min(1)]) });
    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (pessoa?.id && pessoa.uf) {
                this.carregar();
                this.carregarTipos();
            }
        });
    }
    carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.carregando.set(true);
        this.service
            .listar(pessoa.id, pessoa.uf)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (itens) => this.linhas.set(itens),
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.linhas.set([])
            });
    }
    carregarTipos(): void {
        this.service
            .tipos()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((itens) => this.tipos.set(itens));
    }
    novo(): void {
        this.itemEditado = undefined;
        this.form.reset({ quantidade: 1 });
        this.editando = true;
    }
    editar(item: PrestadorRecursoHumano): void {
        this.itemEditado = item;
        this.form.reset({ idRecursoHumano: item.idRecursoHumano, quantidade: item.quantidade });
        this.editando = true;
    }
    salvar(): void {
        const pessoa = this.pessoa();
        if (!pessoa || this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const valor = this.form.getRawValue();
        const request: PrestadorRecursoHumanoRequest = {
            id: pessoa.id,
            uf: pessoa.uf,
            idRecursoHumano: valor.idRecursoHumano!,
            quantidade: valor.quantidade!,
            dtInclusao: this.itemEditado?.dtInclusao,
            dtInclusaoOriginal: this.itemEditado?.dtInclusao
        };
        this.salvando.set(true);
        const operacao = this.itemEditado ? this.service.atualizar(request) : this.service.criar(request);
        operacao
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.editando = false;
                this.carregar();
                this.notificacoes.notificar({ severity: 'success', summary: 'Recurso humano salvo.' });
            });
    }
    confirmarRemocao(item: PrestadorRecursoHumano): void {
        const pessoa = this.pessoa();
        if (!pessoa) return;
        this.confirmation.confirm({
            message: `Remover ${item.recursoHumano}?`,
            accept: () =>
                this.service
                    .remover({ id: pessoa.id, uf: pessoa.uf, idRecursoHumano: item.idRecursoHumano, quantidade: item.quantidade!, dtInclusao: item.dtInclusao })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
                    .subscribe(() => {
                        this.carregar();
                        this.notificacoes.notificar({ severity: 'success', summary: 'Recurso humano removido.' });
                    })
        });
    }
    get total(): number {
        return this.linhas().reduce((valor, item) => valor + (item.quantidade ?? 0), 0);
    }
}
